import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

// ============ TYPES ============

interface StructureDefinition {
  type: string;
  name: string;
  description: string;
  baseCost: number;
  buildTimeMs: number;
  maxHealth: number;
  maxLevel: number;
  widthTiles: number;
  heightTiles: number;
  effects: Record<number, { type: string; value: number; description: string }[]>;
}

interface Structure {
  id: string;
  type: string;
  level: number;
  offsetX: number;
  offsetY: number;
  isBuilding: boolean;
  buildStartedAt: string | null;
  health: number;
  maxHealth: number;
}

interface ResourceNodeDefinition {
  type: string;
  name: string;
  description: string;
  baseValue: number;
  maxAmount: number;
  regenerationRate: number;
  harvestTime: number;
  biomes: string[];
  widthTiles: number;
  heightTiles: number;
}

interface ResourceNode {
  id: string;
  type: string;
  offsetX: number;
  offsetY: number;
  currentAmount: number;
  maxAmount: number;
  variant: number;
}

interface TerritoryManagementPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  islandId: string;
  islandName: string;
  isOwner: boolean;
  playerPieces: number;
}

// ============ COMPONENT ============

export const TerritoryManagementPanel: React.FC<TerritoryManagementPanelProps> = ({
  isOpen,
  onClose,
  socket,
  islandId,
  islandName,
  isOwner,
  playerPieces,
}) => {
  const [activeTab, setActiveTab] = useState<'structures' | 'resources'>('structures');
  const [structureDefinitions, setStructureDefinitions] = useState<Record<string, StructureDefinition>>({});
  const [resourceDefinitions, setResourceDefinitions] = useState<Record<string, ResourceNodeDefinition>>({});
  const [structures, setStructures] = useState<Structure[]>([]);
  const [resourceNodes, setResourceNodes] = useState<ResourceNode[]>([]);
  const [selectedStructure, setSelectedStructure] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [isHarvesting, setIsHarvesting] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Fetch definitions and data on mount
  useEffect(() => {
    if (!socket || !isOpen) return;

    socket.emit('territory:getStructureDefinitions');
    socket.emit('territory:getResourceDefinitions');
    socket.emit('territory:getStructures', { islandId });
    socket.emit('territory:getResourceNodes', { islandId });

    // If no resource nodes exist, generate them
    socket.emit('territory:generateResources', { islandId });

    const handleStructureDefinitions = (data: { definitions: Record<string, StructureDefinition> }): void => {
      setStructureDefinitions(data.definitions);
    };

    const handleResourceDefinitions = (data: { definitions: Record<string, ResourceNodeDefinition> }): void => {
      setResourceDefinitions(data.definitions);
    };

    const handleStructures = (data: { islandId: string; structures: Structure[] }): void => {
      if (data.islandId === islandId) {
        setStructures(data.structures);
      }
    };

    const handleResourceNodes = (data: { islandId: string; nodes: ResourceNode[] }): void => {
      if (data.islandId === islandId) {
        setResourceNodes(data.nodes);
      }
    };

    const handleBuildSuccess = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('territory:getStructures', { islandId });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleBuildError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleUpgradeSuccess = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('territory:getStructures', { islandId });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleUpgradeError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleHarvestSuccess = (data: { message: string; value: number }): void => {
      setMessage({ text: `${data.message} (+₱${data.value})`, type: 'success' });
      setIsHarvesting(false);
      socket.emit('territory:getResourceNodes', { islandId });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleHarvestError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setIsHarvesting(false);
      setTimeout(() => setMessage(null), 3000);
    };

    const handleStructureCompleted = (data: { islandId: string }): void => {
      if (data.islandId === islandId) {
        socket.emit('territory:getStructures', { islandId });
      }
    };

    socket.on('territory:structureDefinitions', handleStructureDefinitions);
    socket.on('territory:resourceDefinitions', handleResourceDefinitions);
    socket.on('territory:structures', handleStructures);
    socket.on('territory:resourceNodes', handleResourceNodes);
    socket.on('territory:buildSuccess', handleBuildSuccess);
    socket.on('territory:buildError', handleBuildError);
    socket.on('territory:upgradeSuccess', handleUpgradeSuccess);
    socket.on('territory:upgradeError', handleUpgradeError);
    socket.on('territory:harvestSuccess', handleHarvestSuccess);
    socket.on('territory:harvestError', handleHarvestError);
    socket.on('territory:structureCompleted', handleStructureCompleted);

    return () => {
      socket.off('territory:structureDefinitions', handleStructureDefinitions);
      socket.off('territory:resourceDefinitions', handleResourceDefinitions);
      socket.off('territory:structures', handleStructures);
      socket.off('territory:resourceNodes', handleResourceNodes);
      socket.off('territory:buildSuccess', handleBuildSuccess);
      socket.off('territory:buildError', handleBuildError);
      socket.off('territory:upgradeSuccess', handleUpgradeSuccess);
      socket.off('territory:upgradeError', handleUpgradeError);
      socket.off('territory:harvestSuccess', handleHarvestSuccess);
      socket.off('territory:harvestError', handleHarvestError);
      socket.off('territory:structureCompleted', handleStructureCompleted);
    };
  }, [socket, isOpen, islandId]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleBuildStructure = useCallback((structureType: string): void => {
    if (!socket) return;
    socket.emit('territory:buildStructure', { islandId, structureType });
  }, [socket, islandId]);

  const handleUpgradeStructure = useCallback((structureId: string): void => {
    if (!socket) return;
    socket.emit('territory:upgradeStructure', { structureId });
  }, [socket]);

  const handleHarvestResource = useCallback((nodeId: string, amount: number): void => {
    if (!socket || isHarvesting) return;
    setIsHarvesting(true);
    socket.emit('territory:harvestResource', { nodeId, amount });
  }, [socket, isHarvesting]);

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const getStructureIcon = (type: string): string => {
    switch (type) {
      case 'watchtower': return '[W]';
      case 'dock': return '[D]';
      case 'warehouse': return '[S]';
      case 'cannon_battery': return '[C]';
      case 'refinery': return '[R]';
      case 'shipyard': return '[Y]';
      default: return '[?]';
    }
  };

  const getResourceIcon = (type: string): string => {
    switch (type) {
      case 'timber': return '[T]';
      case 'ore': return '[O]';
      case 'sulfur': return '[S]';
      case 'whale_oil': return '[W]';
      case 'herbs': return '[H]';
      case 'gems': return '[G]';
      default: return '[?]';
    }
  };

  if (!isOpen) return null;

  const builtStructureTypes = new Set(structures.map(s => s.type));

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          width: '700px',
          maxHeight: '85vh',
          overflow: 'hidden',
          fontFamily: '"Sometype Mono", monospace',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold' }}>
            TERRITORY: {islandName}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#ffd700', fontSize: '12px' }}>
              {playerPieces.toLocaleString()}₱
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: '1px solid #f85149',
                color: '#f85149',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                fontFamily: '"Sometype Mono", monospace',
                fontWeight: 'bold',
              }}
            >
              x
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <button
            onClick={() => setActiveTab('structures')}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: activeTab === 'structures' ? '#0d1117' : 'transparent',
              color: activeTab === 'structures' ? '#58a6ff' : '#8b949e',
              border: 'none',
              borderBottom: activeTab === 'structures' ? '2px solid #58a6ff' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: '"Sometype Mono", monospace',
            }}
          >
            STRUCTURES
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: activeTab === 'resources' ? '#0d1117' : 'transparent',
              color: activeTab === 'resources' ? '#3fb950' : '#8b949e',
              border: 'none',
              borderBottom: activeTab === 'resources' ? '2px solid #3fb950' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: '"Sometype Mono", monospace',
            }}
          >
            RESOURCES
          </button>
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '10px 16px',
              backgroundColor: message.type === 'success' ? '#1c3d2e' : '#3d1c1c',
              borderBottom: `1px solid ${message.type === 'success' ? '#3fb950' : '#f85149'}`,
              color: message.type === 'success' ? '#3fb950' : '#f85149',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {message.text}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {activeTab === 'structures' ? (
            <div>
              {/* Built Structures */}
              {structures.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    BUILT STRUCTURES
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {structures.map((structure) => {
                      const def = structureDefinitions[structure.type];
                      if (!def) return null;
                      const isSelected = selectedStructure === structure.id;
                      const canUpgrade = isOwner && structure.level < def.maxLevel && !structure.isBuilding;
                      const upgradeCost = Math.floor(def.baseCost * (1 + structure.level * 0.5));

                      return (
                        <div
                          key={structure.id}
                          onClick={() => setSelectedStructure(isSelected ? null : structure.id)}
                          style={{
                            backgroundColor: isSelected ? '#1c2128' : '#161b22',
                            border: `1px solid ${isSelected ? '#58a6ff' : '#30363d'}`,
                            padding: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold' }}>
                                {getStructureIcon(structure.type)}
                              </span>
                              <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                                {def.name}
                              </span>
                              <span style={{ color: '#8b949e', fontSize: '11px' }}>
                                Lv.{structure.level}
                              </span>
                            </div>
                            {structure.isBuilding && (
                              <span style={{ color: '#f0883e', fontSize: '10px' }}>
                                BUILDING...
                              </span>
                            )}
                          </div>

                          {isSelected && (
                            <div style={{ marginTop: '10px' }}>
                              <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
                                {def.effects[structure.level]?.map((e, i) => (
                                  <div key={i}>{e.description}</div>
                                ))}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ color: '#8b949e', fontSize: '10px' }}>
                                  HP: {structure.health}/{structure.maxHealth}
                                </span>
                                {canUpgrade && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpgradeStructure(structure.id);
                                    }}
                                    disabled={playerPieces < upgradeCost}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: playerPieces >= upgradeCost ? '#238636' : '#21262d',
                                      color: playerPieces >= upgradeCost ? '#ffffff' : '#8b949e',
                                      border: 'none',
                                      cursor: playerPieces >= upgradeCost ? 'pointer' : 'not-allowed',
                                      fontSize: '10px',
                                      fontWeight: 'bold',
                                      fontFamily: '"Sometype Mono", monospace',
                                    }}
                                  >
                                    UPGRADE ({upgradeCost.toLocaleString()}₱)
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available Structures to Build */}
              {isOwner && (
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    AVAILABLE TO BUILD
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.values(structureDefinitions)
                      .filter((def) => !builtStructureTypes.has(def.type))
                      .map((def) => {
                        const canAfford = playerPieces >= def.baseCost;

                        return (
                          <div
                            key={def.type}
                            style={{
                              backgroundColor: '#161b22',
                              border: '1px solid #30363d',
                              padding: '12px',
                              opacity: canAfford ? 1 : 0.6,
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold' }}>
                                    {getStructureIcon(def.type)}
                                  </span>
                                  <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                                    {def.name}
                                  </span>
                                </div>
                                <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '6px' }}>
                                  {def.description}
                                </div>
                                <div style={{ color: '#6e7681', fontSize: '10px' }}>
                                  Build time: {formatTime(def.buildTimeMs)}
                                </div>
                              </div>
                              <button
                                onClick={() => handleBuildStructure(def.type)}
                                disabled={!canAfford}
                                style={{
                                  padding: '8px 14px',
                                  backgroundColor: canAfford ? '#238636' : '#21262d',
                                  color: canAfford ? '#ffffff' : '#8b949e',
                                  border: 'none',
                                  cursor: canAfford ? 'pointer' : 'not-allowed',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  fontFamily: '"Sometype Mono", monospace',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                BUILD ({def.baseCost.toLocaleString()}₱)
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {!isOwner && structures.length === 0 && (
                <div style={{ color: '#8b949e', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                  No structures built on this territory.
                </div>
              )}
            </div>
          ) : (
            // Resources Tab
            <div>
              {resourceNodes.length === 0 ? (
                <div style={{ color: '#8b949e', fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                  No resource nodes on this island.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {resourceNodes.map((node) => {
                    const def = resourceDefinitions[node.type];
                    if (!def) return null;
                    const isSelected = selectedResource === node.id;
                    const canHarvest = node.currentAmount > 0;
                    const harvestValue = Math.min(10, node.currentAmount) * def.baseValue;

                    return (
                      <div
                        key={node.id}
                        onClick={() => setSelectedResource(isSelected ? null : node.id)}
                        style={{
                          backgroundColor: isSelected ? '#1c2128' : '#161b22',
                          border: `1px solid ${isSelected ? '#3fb950' : '#30363d'}`,
                          padding: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#3fb950', fontSize: '14px', fontWeight: 'bold' }}>
                              {getResourceIcon(node.type)}
                            </span>
                            <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                              {def.name}
                            </span>
                          </div>
                          <span style={{ color: canHarvest ? '#3fb950' : '#f85149', fontSize: '11px' }}>
                            {node.currentAmount}/{node.maxAmount}
                          </span>
                        </div>

                        {isSelected && (
                          <div style={{ marginTop: '10px' }}>
                            <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
                              {def.description}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ color: '#6e7681', fontSize: '10px' }}>
                                <div>Value: {def.baseValue} pieces/unit</div>
                                <div>Regen: {def.regenerationRate}/hour</div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHarvestResource(node.id, 10);
                                }}
                                disabled={!canHarvest || isHarvesting}
                                style={{
                                  padding: '8px 14px',
                                  backgroundColor: canHarvest && !isHarvesting ? '#238636' : '#21262d',
                                  color: canHarvest && !isHarvesting ? '#ffffff' : '#8b949e',
                                  border: 'none',
                                  cursor: canHarvest && !isHarvesting ? 'pointer' : 'not-allowed',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  fontFamily: '"Sometype Mono", monospace',
                                }}
                              >
                                {isHarvesting ? 'HARVESTING...' : `HARVEST (+₱${harvestValue})`}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
                  RESOURCE INFO
                </div>
                <div style={{ color: '#6e7681', fontSize: '10px', lineHeight: '1.5' }}>
                  Resources regenerate over time. Sail to this island and harvest them to earn pieces.
                  Different biomes produce different resources.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
